import { useState, useEffect, createContext, useContext } from 'react';
import { Outlet, useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  Button,
  Badge,
} from '@machinemetrics/mm-react-components';
import { 
  Factory, 
  LayoutGrid, 
  Package, 
  BarChart3, 
  Settings, 
  ChevronLeft,
  Loader2,
  Menu,
  X,
} from 'lucide-react';

// API base URL
const API_BASE = '/api';

// Company context
const CompanyContext = createContext(null);

export function useCompany() {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within CompanyLayout');
  }
  return context;
}

export default function CompanyLayout() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Fetch company data
  useEffect(() => {
    const fetchCompany = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/companies/${companyId}`);
        if (!res.ok) throw new Error('Company not found');
        const data = await res.json();
        // API returns { company: {...} }
        setCompany(data.company || data);
      } catch (err) {
        console.error('Error fetching company:', err);
        setError(err.message);
        setCompany(null);
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchCompany();
    }
  }, [companyId]);

  // Navigation items
  const navItems = [
    { 
      path: `/company/${companyId}/floor`, 
      label: 'Shop Floor', 
      icon: LayoutGrid,
      description: 'Isometric factory view'
    },
    { 
      path: `/company/${companyId}/erp`, 
      label: 'ERP', 
      icon: Package,
      description: 'Parts, routing, work orders'
    },
    { 
      path: `/company/${companyId}/metrics`, 
      label: 'Metrics', 
      icon: BarChart3,
      description: 'KPIs and dashboards'
    },
    { 
      path: `/company/${companyId}/settings`, 
      label: 'Settings', 
      icon: Settings,
      description: 'Events and configuration'
    },
  ];

  const isActive = (path) => location.pathname.startsWith(path);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading company...</span>
        </div>
      </div>
    );
  }

  if (error && !company) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <p className="text-destructive mb-2 font-medium">{error}</p>
          <p className="text-muted-foreground text-sm mb-4">
            Company ID: <code className="bg-muted px-1 rounded">{companyId}</code>. If you just created this company, check that the app and the server use the same backend.
          </p>
          <Button onClick={() => navigate('/launcher')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Launcher
          </Button>
        </div>
      </div>
    );
  }

  return (
    <CompanyContext.Provider value={{ company, setCompany, loading }}>
      <div className="min-h-screen bg-background flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden md:flex w-64 flex-col border-r border-zinc-700/50 bg-zinc-900">
          {/* Company Header */}
          <div className="p-4 border-b border-zinc-700/50">
            <Link to="/launcher" className="flex items-center gap-2 text-zinc-400 hover:text-zinc-200 transition-colors mb-3">
              <ChevronLeft className="w-4 h-4" />
              <span className="text-sm">Back to Launcher</span>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                <Factory className="w-5 h-5 text-violet-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-zinc-100 truncate">{company?.name}</h2>
                <Badge variant="outline" className="text-xs">
                  {company?.mode}
                </Badge>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-violet-600 text-white'
                    : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <div>
                  <div className="font-medium">{item.label}</div>
                  <div className={`text-xs ${isActive(item.path) ? 'text-violet-200' : 'text-zinc-500'}`}>
                    {item.description}
                  </div>
                </div>
              </Link>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-zinc-700/50 text-xs text-zinc-500">
            Manufacturing Hero v0.1
          </div>
        </aside>

        {/* Mobile Header */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-zinc-900 border-b border-zinc-700/50">
          <div className="flex items-center justify-between p-3">
            <div className="flex items-center gap-2">
              <Factory className="w-6 h-6 text-violet-400" />
              <span className="font-semibold text-zinc-100">{company?.name}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(!sidebarOpen)}>
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div 
            className="md:hidden fixed inset-0 z-40 bg-black/50"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Mobile Sidebar */}
        <aside className={`md:hidden fixed top-0 left-0 bottom-0 w-64 z-50 bg-zinc-900 transform transition-transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className="p-4 border-b border-zinc-700/50 mt-14">
            <Link 
              to="/launcher" 
              className="flex items-center gap-2 text-zinc-400 hover:text-zinc-200"
              onClick={() => setSidebarOpen(false)}
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="text-sm">Back to Launcher</span>
            </Link>
          </div>
          <nav className="p-3 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-violet-600 text-white'
                    : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 md:ml-0 mt-14 md:mt-0">
          <Outlet />
        </main>
      </div>
    </CompanyContext.Provider>
  );
}
